import './popupRichiestaFerie.scss';

import React, { useState, useImperativeHandle, useEffect, useRef } from "react";
import { Popup, Form, Button } from "devextreme-react";
import { Position, ToolbarItem } from "devextreme-react/popup";
import { CustomRule, FormRef, Item } from "devextreme-react/form";
import { ClickEvent } from "devextreme/ui/button";
import { useUserSessionContext } from "../../contexts/UserSessionContext";
import { useScreenSize } from "../../utils/media-query";

import useApiConfiguration from "../../apiConfig";
import { CausaliCDLApi, RichiesteCausaliCDLApi, RichiesteCausaliCdlInsertDTO } from "../../data";

export type PopupRichiestaFerieHandle = {
    show: (giorni: string) => void
};

type Props = {
    onChange: () => void
};

type State = {
    giorni: string,
    visible: boolean
};

const PopupRichiestaFerie: React.ForwardRefRenderFunction<PopupRichiestaFerieHandle, Props> = (props, ref) => {
    const emptyData: RichiesteCausaliCdlInsertDTO = {
        idPersonale: null,
        idCausaleRichiesta: null,
        dataDa: undefined,
        dataA: undefined,
        oraInizio: undefined,
        note: null,
        ore: 0.5
    };

    const formRef = useRef<FormRef>(null);
    const apiConfiguration = useApiConfiguration();
    const { user }  = useUserSessionContext();
    const { isLarge } = useScreenSize();

    const [state, setState] = useState<State>({ giorni: '', visible: false });
    const [formData, setFormData] = useState<RichiesteCausaliCdlInsertDTO>();
    const [causaliDataSource, setCausaliDataSource] = useState<any>(null);

    useImperativeHandle(ref, () => ({
        show(giorni) {
            setState({ giorni: giorni, visible: true });
        }
    }));

    function hideInfo() {
        setState({ giorni: '', visible: false });
    };

    function setDateWithTime(date: Date) {
        if (!date) return null;

        const newDate = new Date(formData.dataDa);
        newDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
        return newDate;
    };

    function setTimeToDate(date: Date, ore: number) {
        if (!date) return null;

        const newDate = new Date(date);
        newDate.setHours(ore, 0, 0);
        return newDate;
    };

    useEffect(() => {
        if (state.visible) {
            var api = new CausaliCDLApi(apiConfiguration);

            api.getCausaliCdlRichiedibili().then((data) => setCausaliDataSource(data));

            setFormData({
                ...emptyData,
                idPersonale: user.id
            })
        }
    }, [state.giorni, state.visible]);

    const onFieldDataChanged = (e) => {
        if (state.giorni === "M" && (e.dataField == "dataDa" || e.dataField == "dataA"))
            formRef.current?.instance().validate();
    };

    const validateDataDa = (e) => {
        const dataDa = e.value;

        if ((dataDa && !formData.dataA) || (!dataDa && formData.dataA))
            return false;

        if (dataDa && formData.dataA)
            return setTimeToDate(dataDa, 8) <= setTimeToDate(formData.dataA, 8);

        return true;
    };

    const validateDataA = (e) => {
        const dataA = e.value;

        if ((formData.dataDa && !dataA) || (!formData.dataDa && dataA))
            return false;

        if (formData.dataDa && dataA)
            return setTimeToDate(formData.dataDa, 8) <= setTimeToDate(dataA, 8);

        return true;
    };

    const insert = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            if (state.giorni === 'S') {
                var oraInizio = setDateWithTime(formData.oraInizio);

                formData.dataA = formData.dataDa;
                formData.oraInizio = oraInizio;
            } else {
                formData.oraInizio = null;
                formData.ore = null;
            }
            
            var api = new RichiesteCausaliCDLApi(apiConfiguration);
            api.insertRichiestaCausaleCdl({
                richiesteCausaliCdlInsertDTO: formData
            }).then(() => props.onChange())
            .finally(() => hideInfo())
        }
    };

    return (
        <React.Fragment>
            <Popup
                visible={state.visible}
                dragEnabled={false}
                hideOnOutsideClick
                showCloseButton
                fullScreen={false}
                showTitle
                title={state.giorni === 'S' ? 'Giorno singolo' : 'Giorni multipli'}
                container=".dx-viewport"
                height={"auto"}
                onHidden={hideInfo}
            >
                <Position at="center" my="center" of="" collision="fit" />

                <ToolbarItem widget="dxButton" toolbar="bottom" location="center">
                    <Button
                        onClick={insert}
                        icon="check"
                        text="Salva"
                        type="default"
                        stylingMode="contained"
                    />
                </ToolbarItem>

                <Form ref={formRef} formData={formData} labelMode="floating" colCount={ isLarge ? 2 : 1 } 
                    id="form-richiesta-ferie" onFieldDataChanged={onFieldDataChanged}
                >
                    <Item visible={false} dataField="idPersonale" />

                    <Item dataField="idCausaleRichiesta" label={{ text: "Causale" }} 
                        isRequired={true} editorType="dxSelectBox"
                        editorOptions={{
                            dataSource: causaliDataSource,
                            displayExpr: 'descrizione',
                            valueExpr: 'idCausaleCdL',
                            searchEnabled: true,
                            showClearButton: true
                        }}
                    />

                    { isLarge && <Item itemType="empty" /> }

                    {
                        state.giorni === 'S' && (
                            <React.Fragment>
                                <Item colSpan={1} dataField="dataDa" isRequired={true}
                                    label={{ text: "Data" }} editorType="dxDateBox"
                                    editorOptions={{
                                        type: "date",
                                        displayFormat: isLarge ? "EEEE d MMMM yyyy" : "dd/MM/yyyy",
                                        openOnFieldClick: true
                                    }}
                                />

                                <Item colSpan={1} dataField="ore" isRequired={true}
                                    label={{ text: "Ore Richieste" }} editorType="dxNumberBox"
                                    editorOptions={{
                                        default: 0.5
                                    }}
                                />

                                <Item colSpan={1} dataField="oraInizio" isRequired={true}
                                    label={{ text: "Ora Inizio" }} editorType="dxDateBox"
                                    editorOptions={{
                                        displayFormat: "HH:mm",
                                        type: "time"
                                    }}
                                />

                                { isLarge && <Item itemType="empty" /> }
                            </React.Fragment>
                        )
                    }

                    {
                        state.giorni === 'M' && (
                            <React.Fragment>
                                <Item colSpan={1} dataField='dataDa' isRequired={true} 
                                    label={{ text: "Data Da" }} editorType='dxDateBox'
                                    editorOptions={{
                                        type: "date",
                                        displayFormat: isLarge ? "EEEE d MMMM yyyy" : "dd/MM/yyyy",
                                        openOnFieldClick: true
                                    }}
                                >
                                    <CustomRule 
                                        reevaluate={true}
                                        validationCallback={validateDataDa}
                                        message='Data non valida'
                                    />
                                </Item>

                                <Item colSpan={1} dataField='dataA' isRequired={true} 
                                    label={{ text: "Data A" }} editorType='dxDateBox'
                                    editorOptions={{
                                        type: "date",
                                        displayFormat: isLarge ? "EEEE d MMMM yyyy" : "dd/MM/yyyy",
                                        openOnFieldClick: true
                                    }}
                                >
                                    <CustomRule 
                                        reevaluate={true}
                                        validationCallback={validateDataA}
                                        message='Data non valida'
                                    />
                                </Item>
                            </React.Fragment>
                        )
                    }

                    <Item colSpan={isLarge ? 2 : 1} dataField="note" 
                        editorType="dxTextArea" cssClass="dx-textarea-custom"
                        editorOptions={{
                            maxLength: 1500,
                            autoResizeEnabled: true
                        }}
                    />
                </Form>
            </Popup>
        </React.Fragment>
    );
};

export default React.forwardRef(PopupRichiestaFerie);